import { Link } from "react-router-dom";
import useLogoUrl from "@/hooks/useLogoUrl";
import { siteData } from "@/store/siteData";

export default function Footer() {
  const logoUrl = useLogoUrl();
  const settings = siteData.getGeneralSettings();
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#FFFFFF] border-t border-[#E2E8F0]">
      {/* Top Section */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="lg:col-span-2">
          <img
            src={logoUrl}
            alt={settings.companyName}
            className="h-14 w-auto object-contain mb-5"
          />
          <p className="text-[#64748B] text-sm leading-relaxed max-w-sm">
            {settings.tagline || `10+ yıllık tecrübemizle yüzlerce kuruma ağ altyapısı, siber güvenlik, bulut ve teknik servis çözümleri sunduk. Uçtan uca bilişim çözüm ortağınız olarak işletmenize değer katıyoruz.`}
          </p>
          <div className="flex items-center gap-4 mt-6">
            {settings.socialInstagram && (
              <a href={settings.socialInstagram.startsWith('http') ? settings.socialInstagram : `https://instagram.com/${settings.socialInstagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-[#E2E8F0] text-[#64748B] hover:border-[#3B82F6] hover:text-[#334155] transition-all duration-200 cursor-pointer">
                <i className="ri-instagram-line text-base"></i>
              </a>
            )}
            {settings.socialLinkedin && (
              <a href={settings.socialLinkedin.startsWith('http') ? settings.socialLinkedin : `https://linkedin.com/company/${settings.socialLinkedin}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-[#E2E8F0] text-[#64748B] hover:border-[#3B82F6] hover:text-[#334155] transition-all duration-200 cursor-pointer">
                <i className="ri-linkedin-box-line text-base"></i>
              </a>
            )}
            {settings.socialYoutube && (
              <a href={settings.socialYoutube.startsWith('http') ? settings.socialYoutube : `https://youtube.com/@${settings.socialYoutube.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-[#E2E8F0] text-[#64748B] hover:border-[#3B82F6] hover:text-[#334155] transition-all duration-200 cursor-pointer">
                <i className="ri-youtube-line text-base"></i>
              </a>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-[#0F172A] text-sm font-semibold tracking-widest mb-5 uppercase">Hızlı Linkler</h4>
          <ul className="space-y-3">
            {[
              { label: "Ana Sayfa", to: "/" },
              { label: "Hakkımızda", to: "/hakkimizda" },
              { label: "Ürünlerimiz", to: "/urunler" },
              { label: "Stratejik Ortaklarımız", to: "/stratejik-ortaklarimiz" },
              { label: "İletişim", to: null, id: "iletisim" },
            ].map((item) => (
              <li key={item.label}>
                {item.to ? (
                  <Link to={item.to} className="text-[#64748B] hover:text-[#334155] text-sm transition-colors cursor-pointer">
                    {item.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => item.id && scrollTo(item.id)}
                    className="text-[#64748B] hover:text-[#334155] text-sm transition-colors cursor-pointer text-left"
                  >
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-[#0F172A] text-sm font-semibold tracking-widest mb-5 uppercase">İletişim</h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-map-pin-line text-[#64748B] text-base"></i>
              </div>
              <span className="text-[#64748B] text-sm leading-relaxed whitespace-pre-line">
                {settings.address}
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-[#64748B] text-base"></i>
              </div>
              <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="text-[#64748B] hover:text-[#334155] text-sm transition-colors cursor-pointer">
                {settings.phone}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-[#64748B] text-base"></i>
              </div>
              <a href={`mailto:${settings.email}`} className="text-[#64748B] hover:text-[#334155] text-sm transition-colors cursor-pointer">
                {settings.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Brand Logo */}
      <div className="border-t border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex justify-center">
          <div className="bg-white rounded-2xl px-10 py-6 shadow-lg">
            <img
              src="/c2-logo.png"
              alt="C2"
              className="h-12 w-auto object-contain select-none"
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#94A3B8] text-xs">
            © {new Date().getFullYear()} {settings.companyName}. Tüm hakları saklıdır.
          </p>
          <div className="flex items-center gap-5">
            <a href="#" className="text-[#94A3B8] hover:text-[#64748B] text-xs transition-colors cursor-pointer">Gizlilik Politikası</a>
            <a href="#" className="text-[#94A3B8] hover:text-[#64748B] text-xs transition-colors cursor-pointer">KVKK Aydınlatma Metni</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
